<template>
  <section class="alternative-matches-section">
    <h3>Et si tu avais joué contre&nbsp;?</h3>
    <p class="alternative-matches-section__subtitle">
      Résultats face à chaque adversaire possible, bonus retirés de part et d'autre
    </p>

    <div v-if="isLoading" class="loading placeholder-glow" aria-hidden="true">
      <span class="placeholder col-12" />
      <span class="placeholder col-12" />
      <span class="placeholder col-12" />
    </div>

    <div v-else-if="!entries.length" class="no-data">
      <p>Aucun résultat alternatif à afficher pour la dernière journée.</p>
    </div>

    <div v-else>
      <league-outcomes-summary :entries="entries" />

      <alternative-match-display
        v-for="entry in entries"
        :key="entry.id"
        :match="entry.match"
        :outcome="entry.outcome"
        :is-real-match="entry.isRealMatch"
        :class="entry.isRealMatch ? 'mb-4' : 'mb-2'"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, toRef } from "vue";

import AlternativeMatchDisplay from "@/components/alternativeMatches/AlternativeMatchDisplay.vue";
import LeagueOutcomesSummary from "@/components/alternativeMatches/LeagueOutcomesSummary.vue";
import { useLeagueCache } from "@/use/useLeagueCache";
import { useMPG } from "@/use/useMPG";
import { buildAlternativeMatches } from "@/utils/alternativeMatches/alternativeMatchesCalculator";
import { getDivisionId, getLastPlayedGameWeek } from "@/utils/leagues/leagueContext";

const props = defineProps({
  league: {
    type: Object,
    required: true,
  },
});

const { getGameWeekMatches, getLeagueInfos, getTeamInfos } = useMPG();

/**
 * Renvoi le nom d'une équipe, ou undefined si sa récupération échoue
 * Le nom est purement cosmétique : buildAlternativeMatches retombe déjà sur
 * un nom par défaut quand il est absent, un échec isolé ne doit donc pas
 * remonter
 * @param {String} teamId l'identifiant de l'équipe
 * @returns {Promise<String|undefined>} le nom de l'équipe
 */
const getTeamNameSafely = async (teamId) => {
  try {
    const teamInfos = await getTeamInfos(teamId);
    return teamInfos?.name;
  } catch {
    return undefined;
  }
};

/**
 * Renvoi les noms des équipes ayant joué la journée
 * @param {Array} divisionMatches les matchs de la journée
 * @returns {Promise<Map>} les noms indexés par identifiant d'équipe
 */
const getTeamNames = async (divisionMatches) => {
  const teamIds = divisionMatches.flatMap(({ home, away }) => [home.teamId, away.teamId]);
  const teamsNames = await Promise.all(teamIds.map(getTeamNameSafely));
  return new Map(teamIds.map((teamId, index) => [teamId, teamsNames[index]]));
};

/**
 * Renvoi les confrontations alternatives d'une ligue
 * @param {Object} league la ligue issue des données live
 * @returns {Promise<Array>} les confrontations, vide si la journée n'est pas exploitable
 */
const getLeagueEntries = async (league) => {
  const divisionId = getDivisionId(league);
  const gameWeek = getLastPlayedGameWeek(league);
  if (! divisionId || ! league.userTeamId || ! gameWeek) {
    return [];
  }

  const divisionMatches = (await getGameWeekMatches(divisionId, gameWeek)).filter(({ finalResult }) => finalResult);
  if (divisionMatches.length === 0) {
    return [];
  }

  const [{ isOneWay }, teamNames] = await Promise.all([
    getLeagueInfos(league.leagueId),
    getTeamNames(divisionMatches),
  ]);

  return buildAlternativeMatches({
    divisionMatches,
    userTeamId: league.userTeamId,
    isOneWay,
    teamNames,
  });
};

// Une ligue en échec affiche le message d'absence de résultats plutôt que
// de faire planter l'écran ; elle n'est pas mise en cache, pour rester
// réessayable au prochain passage sur la ligue
const league = toRef(props, "league");
const { isLoading, value } = useLeagueCache(league, getLeagueEntries, "résultats alternatifs");
const entries = computed(() => value.value ?? []);
</script>

<style lang="scss" scoped>
.alternative-matches-section {
  padding: 20px 0;

  h3 {
    text-align: center;
    margin-bottom: 5px;
    font-size: 1.1rem;
  }

  &__subtitle {
    text-align: center;
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 20px;
  }
}

.loading, .no-data {
  text-align: center;
  padding: 40px 20px;
}

.loading .placeholder {
  height: 48px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.no-data {
  color: #666;
}
</style>